import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { BREAKPOINTS } from '../constants';
import { trackProductEvent } from '../analytics/productAnalytics';

const QuickFilterBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin: 0;
`;

const FilterButton = styled.button<{ $active?: boolean }>`
  min-height: 34px;
  border-radius: 7px;
  border: 1px solid ${({ $active }) => $active ? 'var(--primary)' : 'var(--border)'};
  background: ${({ $active }) => $active ? 'var(--primary)' : 'var(--surface)'};
  color: ${({ $active }) => $active ? '#fff' : 'var(--text)'};
  padding: 0.3rem 0.58rem;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 8px var(--shadow);

  &:hover {
    background: ${({ $active }) => $active ? 'var(--primary-hover)' : 'color-mix(in srgb, var(--primary) 10%, var(--surface))'};
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    flex: 1 1 calc(50% - 0.45rem);
    min-height: 44px;
  }
`;

const QuickFilters: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRange = searchParams.get('range') || '';
  const selectedEntry = searchParams.get('entry') || '';

  const updateParam = useCallback((key: string, value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    const selected = nextParams.get(key) !== value && Boolean(value);

    if (nextParams.get(key) === value || !value) {
      nextParams.delete(key);
    } else {
      nextParams.set(key, value);
    }

    setSearchParams(nextParams, { replace: true });
    trackProductEvent('discovery_filter_changed', {
      surface: 'quick_filter',
      filter_type: key === 'range' ? 'date_range' : 'entry',
      filter_value: value,
      selected,
    });
  }, [searchParams, setSearchParams]);

  const clearFilters = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('range');
    nextParams.delete('entry');
    setSearchParams(nextParams, { replace: true });
    trackProductEvent('discovery_filter_changed', {
      surface: 'quick_filter',
      filter_type: 'clear',
      filter_value: 'all',
      selected: false,
    });
  };

  const hasQuickFilters = Boolean(selectedRange || selectedEntry);

  return (
    <QuickFilterBar aria-label="Quick filters">
      <FilterButton
        type="button"
        $active={selectedRange === 'today'}
        aria-pressed={selectedRange === 'today'}
        onClick={() => updateParam('range', 'today')}
      >
        Today
      </FilterButton>
      <FilterButton
        type="button"
        $active={selectedRange === 'weekend'}
        aria-pressed={selectedRange === 'weekend'}
        onClick={() => updateParam('range', 'weekend')}
      >
        This Weekend
      </FilterButton>
      <FilterButton
        type="button"
        $active={selectedRange === '7days'}
        aria-pressed={selectedRange === '7days'}
        onClick={() => updateParam('range', '7days')}
      >
        Next 7 Days
      </FilterButton>
      <FilterButton
        type="button"
        $active={selectedRange === '30days'}
        aria-pressed={selectedRange === '30days'}
        onClick={() => updateParam('range', '30days')}
      >
        Next 30 Days
      </FilterButton>
      <FilterButton
        type="button"
        $active={selectedEntry === 'free'}
        aria-pressed={selectedEntry === 'free'}
        onClick={() => updateParam('entry', 'free')}
      >
        Free
      </FilterButton>
      {hasQuickFilters && (
        <FilterButton type="button" onClick={clearFilters}>
          Clear
        </FilterButton>
      )}
    </QuickFilterBar>
  );
};

export default QuickFilters;
